<template>
	<div class="flex flex-col px-6 py-4 min-h-full">
		<GlobalLoader :is-loading="isLoading" />

		<div class="flex flex-col justify-start items-start mt-3">
			<h4 class="font-bold text-3xl text-gray-900">6 Shot Admin</h4>
			<p class="text-gray-500 text-lg">Manage your football prediction platform</p>
		</div>

		<div v-if="!showDetails" class="bg-white p-6 rounded-xl shadow-lg">
			<h4 class="text-2xl font-bold mb-4 text-gray-800">{{ statusFilter ? statusFilter : 'All' }}
				Users</h4>

			<div class="flex justify-between mb-4 gap-4">
				<div class="relative w-full max-w-sm">
					<input v-model="search" type="text"
						placeholder="Search users by email, username, full name, or signup date..."
						class="border border-gray-300 rounded-lg pl-10 pr-4 !text-[16px] py-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150" />
					<q-icon name="search" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
						size="20px" />
					<q-icon v-if="search" name="clear" @click="handleClear"
						class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer"
						size="20px" />
				</div>

				<div class="flex justify-center items-center gap-6">
					<div v-if="statusFilter" @click="handleClearFilter"
						class="bg-orange text-white rounded-md px-3 py-2 flex justify-center items-center cursor-pointer">
						<q-icon name="clear" class="text-white mr-1" size="19px" />Clear Filter
					</div>
					<q-btn-dropdown :label="statusFilter ? statusFilter : 'Filter'" no-caps color="grey-2" class="!h-8"
						icon="filter_list" text-color="black">
						<q-list>
							<q-item clickable v-close-popup @click="onItemClick('Active')"
								:class="statusFilter === 'Active' ? 'bg-gray-200 font-bold' : ''">
								<q-item-section>
									<q-item-label>Active</q-item-label>
								</q-item-section>
							</q-item>

							<q-item clickable v-close-popup @click="onItemClick('Suspended')"
								:class="statusFilter === 'Suspended' ? 'bg-gray-200 font-bold' : ''">
								<q-item-section>
									<q-item-label>Suspended</q-item-label>
								</q-item-section>
							</q-item>
						</q-list>
					</q-btn-dropdown>
				</div>
			</div>

			<q-table :rows="filteredUsers" :columns="userColumns" row-key="name" class="my-sticky-header-table" flat
				bordered :pagination="initialPagination">
				<template v-slot:header="props">
					<q-tr :props="props">
						<q-th v-for="col in props.cols" :key="col.name" :props="props"
							class="!text-[16px] font-extrabold">
							{{ col.label }}
						</q-th>
					</q-tr>
				</template>
				<template v-slot:body-cell-username="props">
					<q-td :props="props">
						<span class="text-[15px]">
							{{ props.row.username }}
						</span>
					</q-td>
				</template>
				<template v-slot:body-cell-fullName="props">
					<q-td :props="props">
						<span class="text-[15px]">
							{{ props.row.fullName }}
						</span>
					</q-td>
				</template>
				<template v-slot:body-cell-email="props">
					<q-td :props="props">
						<span class="text-[15px]">
							{{ props.row.email }}
						</span>
					</q-td>
				</template>
				<template v-slot:body-cell-signupDate="props">
					<q-td :props="props">
						<span class="text-[15px]">
							{{ props.row.signupDate }}
						</span>
					</q-td>
				</template>
				<template v-slot:body-cell-status="props">
					<q-td :props="props">
						<span
							:class="props.row.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
							class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
							{{ props.row.status }}
						</span>
					</q-td>
				</template>

				<template v-slot:body-cell-actions="props">
					<q-td :props="props">
						<button @click="viewDetails(props.row)"
							class="text-gray-600 hover:text-white font-bold !text-[14px] px-3 py-1 rounded-md border border-gray-300 bg-gray-100 hover:bg-gray-500 transition duration-150">
							View Details
						</button>
					</q-td>
				</template>
			</q-table>
		</div>

		<div v-else-if="showPostal">
			<PostalEntry :players="allPlayers" @submit="handleSubmit" @back="backToUserDetails"
				:user-id="selectedUser.id" />
		</div>
		<div v-else class="flex flex-col">
			<div class="flex justify-between items-center my-3">
				<div @click="backToList"
					class="text-lg cursor-pointer flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition duration-150">
					<q-icon name="arrow_back" size="20px" />
					Back
				</div>
				<div class="flex gap-3">
					<div class="relative inline-block">
						<q-tooltip v-if="players_selected">
							Players have already been selected for this user
						</q-tooltip>
						<button @click="handlePostalEntry" :disabled="players_selected"
							class="border border-gray-300 px-6 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed">
							Add Postal Entry
						</button>
					</div>
					<button v-if="selectedUser.status === 'Active'" @click="handleUserStatus('Suspended', selectedUser)"
						class="border border-gray-300 px-6 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition duration-150">
						Suspend User
					</button>
					<button v-else @click="handleUserStatus('Active', selectedUser)"
						class="border border-gray-300 px-6 py-2 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition duration-150">
						Activate User
					</button>
				</div>
			</div>

			<div class="bg-white shadow-lg border border-gray-200 rounded-xl p-6 mb-6">
				<h5 class="text-xl font-extrabold mb-4 border-b pb-2 text-gray-800">User Profile Details
				</h5>

				<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					<div v-for="(label, key) in {
						fullName: 'Full Name',
						email: 'Email ID',
						username: 'Username',
						phone: 'Phone Number',
						dob: 'Date of Birth',
						streetAddress: 'Street Address',
						city: 'City',
						country: 'Country',
						postal: 'Postal Code',
						signupDate: 'Signup Date'
					}" :key="key" class="flex flex-col">
						<label class="text-gray-500 text-sm font-bold">{{ label }}</label>
						<p
							class="text-base font-medium text-gray-800 border border-gray-200 rounded-lg px-3 py-2 max-w-[360px] break-words mt-1 bg-gray-50">
							{{ selectedUser[key] || 'N/A' }}
						</p>
					</div>

					<div class="flex flex-col">
						<label class="text-gray-500 text-sm font-bold">Account Status</label>
						<span
							:class="selectedUser.status === 'Active' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'"
							class="px-4 py-1 text-sm w-fit rounded-xl mt-2 font-medium uppercase">
							{{ selectedUser.status }}
						</span>
					</div>
				</div>
			</div>

			<div class="bg-white shadow-lg border border-gray-200 rounded-xl p-6">
				<h5 class="text-xl font-bold mb-4 border-b pb-2 text-gray-800">Gameweek History
					({{ selectedUser.fullName }})</h5>

				<div v-if="selectedUser.gameweeks && selectedUser.gameweeks.length" class="space-y-4">
					<div v-for="(week, idx) in selectedUser.gameweeks" :key="idx"
						class="border border-gray-100 rounded-lg p-5 relative bg-gray-50">

						<!-- Gameweek Header -->
						<div class="flex justify-between items-center cursor-pointer" @click="toggleExpanded(idx)">
							<div class="flex">
								<!-- Expand/Collapse Icon -->
								<q-icon :name="isExpanded(idx) ? 'expand_less' : 'expand_more'" size="29px"
									class="text-gray-600" />
								<p class="font-medium text-lg mb-1">
									Gameweek {{ week.game_week.week_number }}
									({{ formatDate(week.game_week.week_start_date) }} /
									{{ formatDate(week.game_week.week_end_date) }})
								</p>
							</div>
						</div>

						<!-- Gameweek Status Badge -->
						<span class="absolute top-4 right-4 px-3 py-1 text-sm font-semibold rounded-lg uppercase"
							:class="week.user_stats.points >= 6 && week.user_stats.rank === 1
								? 'bg-green-200 text-green-700'
								: 'bg-red-200 text-red-700'">
							{{ week.user_stats.points >= 6 && week.user_stats.rank === 1 ? 'Win' : 'Loss' }}
						</span>

						<!-- Expandable Content -->
						<transition name="fade">
							<div v-if="isExpanded(idx)" class="mt-4">
								<div class="flex justify-around items-center px-10 mb-4">
									<div class="flex flex-col items-center">
										<p class="text-gray-600 text-lg">Leaderboard Position</p>
										<p class="font-bold text-xl text-indigo-600 !-mt-3">{{ week?.user_stats?.rank ?
											week?.user_stats?.rank : 0 }}
										</p>
									</div>
									<div class="flex flex-col items-center">
										<p class="text-gray-600 text-lg">Goals Scored</p>
										<p class="font-bold text-xl text-indigo-600 !-mt-3">{{
											week?.user_stats?.goals_scored ? week?.user_stats?.goals_scored : 0 }}
										</p>
									</div>
									<div class="flex flex-col items-center">
										<p class="text-gray-600 text-lg">Total Points</p>
										<p class="font-bold text-xl text-indigo-600 !-mt-3">{{ week?.user_stats?.points
											?
											week?.user_stats?.points : 0 }}
										</p>
									</div>
									<div class="flex flex-col items-center">
										<p class="text-gray-600 text-lg">Amount Won</p>
										<p class="font-bold text-xl text-indigo-600 !-mt-3">{{
											week?.weekly_prize?.prize_money ? week?.weekly_prize?.prize_money : 0 }}
										</p>
									</div>
								</div>

								<!-- GameWeek Table -->
								<GameWeekTable :leaderboard="[week]" />
							</div>
						</transition>
					</div>
				</div>
				<div v-else class="text-center py-4 text-gray-500 italic">No gameweek history found for this user.</div>
			</div>
		</div>

		<div v-if="showSuccess" :class="{
			'fixed inset-0 w-full h-full flex items-center justify-center z-[8000] bg-[rgba(0,0,0,0.6)] bg-opacity-95 overflow-hidden': !$q.screen.lt.sm,
			'fixed inset-0 w-full h-full flex flex-col z-[8000] bg-[rgba(0,0,0,0.6)] bg-opacity-60 overflow-hidden': $q.screen.lt.sm
		}">
			<q-card
				:class="{ 'px-10 md:px-28 py-16 rounded-xl flex flex-col gap-8 min-w-[50vw]': !$q.screen.lt.sm, 'px-10 md:px-28 py-10 flex flex-col gap-8 min-w-[50vw] h-full justify-between': $q.screen.lt.sm }">
				<div class="flex items-center justify-between">
					<p class="text-2xl text-[#4F39F6] font-extrabold">
						USER ACCOUNT
					</p>
					<q-btn flat round dense icon="close" color="black" @click="closePage" />
				</div>
				<div class="flex flex-col items-center text-center">
					<div class="flex flex-col items-center justify-center mb-2">
						<SuccessSvg class="w-10 h-10" />
						<p
							class="text-[20px] md:text-[26px] lg:text-[28px] font-bold text-[#4F39F6] my-2">
							User Account {{ selectedStatus === 'Active' ? 'Activated' : 'Suspended' }}
						</p>
					</div>
					<p class="text-[#666666] text-xl text-center my-3 max-w-lg">
						User has been {{ selectedStatus === 'Active' ? 'activated' : 'suspended' }} successfully
					</p>
				</div>
				<div>
					<q-btn label="Okay" type="submit" @click="closePage"
						class="full-width rounded-xl text-white text-xl font-SemiExpandedExtraBold"
						style=" background-color: #4F39F6; font-style: italic" />
				</div>
			</q-card>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, handleError, onMounted } from "vue";
import PostalEntry from "../components/PostalEntry.vue";
import GameWeekTable from "../components/GameWeekTable.vue";
import GlobalLoader from "../components/GlobalLoader.vue";
import SuccessSvg from "../components/svg/SuccessSvg.vue";

// --- State Variables ---
const search = ref("");
const statusFilter = ref("");
const selectedStatus = ref("");
const showDetails = ref(false);
const selectedUser = ref({});
const initialPagination = {
	sortBy: 'username',
	descending: false,
	page: 1,
	rowsPerPage: 6
}
const showPostal = ref(false);
const players_selected = ref(false);
const isLoading = ref(false);
const showSuccess = ref(false);

const expandedWeeks = ref({})

const toggleExpanded = (idx) => {
	expandedWeeks.value[idx] = !expandedWeeks.value[idx]
}

const isExpanded = (idx) => {
	return expandedWeeks.value[idx] || false
}
// Pass player list from parent
const allPlayers = [
	{ label: "Rashford", value: "rashford" },
	{ label: "Saka", value: "saka" },
	{ label: "Haaland", value: "haaland" },
	{ label: "Salah", value: "salah" },
	{ label: "Son", value: "son" },
	{ label: "Watkins", value: "watkins" },
	{ label: "Bowen", value: "bowen" },
	{ label: "Trippier", value: "trippier" },
];

// Handle submit
const handleSubmit = ({ gameweek, players }) => {
	console.log("Selected Gameweek:", gameweek);
	console.log("Selected Players:", players);
};

// Date formatting helper
const formatDate = (dateString) => {
	if (!dateString) return 'N/A';
	const date = new Date(dateString);
	const day = date.getDate();
	const month = date.toLocaleDateString('en-US', { month: 'short' });
	const year = date.getFullYear();
	return `${day} ${month} ${year}`;
};
// --- Quasar Q-Table Columns Definition ---
const userColumns = [
	{ name: 'username', required: true, label: 'Username', align: 'left', field: 'username', format: val => `${val}`, sortable: true },
	{ name: 'fullName', align: 'left', label: 'Full Name', field: 'fullName', sortable: true },
	{ name: 'email', align: 'left', label: 'Email', field: 'email', sortable: true },
	{ name: 'signupDate', align: 'left', label: 'Signup Date', field: 'signupDate', sortable: true },
	{ name: 'status', align: 'left', label: 'Status', field: 'status', sortable: true },
	{ name: 'actions', align: 'left', label: 'Actions', field: 'actions' } // Custom cell template required for this column
];

// --- Mapped Data (from API response) ---
const users = ref([]);

// --- Computed Properties ---
const filteredUsers = computed(() => {
	return users.value.filter(user => {
		// 1. Search filter (checking username, full name, email, and signup date)
		const searchLower = search.value.toLowerCase();
		const matchesSearch =
			user.username.toLowerCase().includes(searchLower) ||
			user.fullName.toLowerCase().includes(searchLower) ||
			user.email.toLowerCase().includes(searchLower) ||
			user.signupDate.toLowerCase().includes(searchLower);

		// 2. Status filter
		const matchesStatus = statusFilter.value ? user.status === statusFilter.value : true;

		return matchesSearch && matchesStatus;
	});
});

onMounted(() => {
	GetUserDetails()
})


const GetUserDetails = async () => {
	isLoading.value = true;
	try {
		const data = await fetch('/admin/users', {
			method: 'GET',
		});

		const val = await data.json();
		console.log("🚀 ~ GetUserDetails ~ data:", val);

		// Map the API response to the expected user structure
		users.value = val.map(user => ({
			id: user.id,
			username: user.username || 'N/A',
			fullName: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A',
			email: user.email || 'N/A',
			status: user.active ? 'Active' : 'Suspended',
			phone: user.phone_number || 'N/A',
			dob: user.date_of_birth || 'N/A',
			streetAddress: user.address || 'N/A',
			city: user.city || 'N/A',
			country: user.country || 'N/A',
			postal: user.zip_code || 'N/A',
			signupDate: user.created_at
				? new Date(user.created_at).toLocaleString('en-GB', {
					day: '2-digit',
					month: 'short',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
					hour12: true
				})
				: 'N/A',
			gameweeks: [] // No gameweek data in response; set to empty array (fetch separately if needed)
		}));
	} catch (error) {
		console.log("🚀 ~ GetUserDetails ~ error:", error);
	} finally {
		isLoading.value = false;
	}
}



const onItemClick = (value) => {
	statusFilter.value = value;
}

const handleClear = () => {
	search.value = '';
}

const handleClearFilter = () => {
	statusFilter.value = '';
}


// --- Methods ---
const viewDetails = async (user) => {
	console.log("🚀 ~ viewDetails ~ user:", user);
	selectedUser.value = { ...user };
	showDetails.value = true;

	const data = await fetch(`admin/users/${user.id}/weekly_details`, {
		method: 'GET',
	});

	const val = await data.json();

	// Map user details if additional fields are present
	if (val.user_details) {
		Object.assign(selectedUser.value, {
			id: val.user_details.id || selectedUser.value.id,
			username: val.user_details.username || selectedUser.value.username || 'N/A',
			fullName: `${val.user_details.first_name || ''} ${val.user_details.last_name || ''}`.trim() || selectedUser.value.fullName || 'N/A',
			email: val.user_details.email || selectedUser.value.email || 'N/A',
			status: val.user_details.active ? 'Active' : 'Suspended',
			phone: val.user_details.phone_number || selectedUser.value.phone || 'N/A',
			dob: val.user_details.date_of_birth || selectedUser.value.dob || 'N/A',
			streetAddress: val.user_details.address || selectedUser.value.streetAddress || 'N/A',
			city: val.user_details.city || selectedUser.value.city || 'N/A',
			country: val.user_details.country || selectedUser.value.country || 'N/A',
			postal: val.user_details.zip_code || selectedUser.value.postal || 'N/A',
			signupDate: val.user_details.created_at
				? new Date(val.user_details.created_at).toLocaleString()
				: selectedUser.value.signupDate || 'N/A',
			gameweeks: val.user_details.gameweeks || selectedUser.value.gameweeks || []
		});
	}

	if (val.weeks && val.weeks.length > 0) {
		selectedUser.value.gameweeks = val.weeks;
	} else {
		selectedUser.value.gameweeks = [];
	}
	players_selected.value = val.players_selected_or_not
};

const handleUserStatus = async (value, selectedId) => {
	try {
		console.log("🚀 ~ handleUserStatus ~ id:", selectedId.id);
		if (!selectedId.id) throw new Error("Missing user ID in handleUserStatus");

		selectedStatus.value = value;
		const action = value === 'Suspended' ? 'suspend' : 'activate';

		const response = await fetch(`/admin/users/${selectedId.id}/${action}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
			},
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(`Failed to ${action} user: ${response.status}\n${errorText}`);
		}

		console.log(`✅ User ${action}d successfully`);
		if (response) {
			await viewDetails(selectedId); // ✅ Pass the id properly
			showSuccess.value = true; // Show success modal after successful operation
		}
	} catch (error) {
		console.error("❌ Error in handleUserStatus:", error);
		alert(`Failed to ${value === 'Suspended' ? 'suspend' : 'activate'} user. Please try again.`);
	}
};

const handlePostalEntry = () => {
	showPostal.value = true;
}

const backToList = () => {
	showDetails.value = false;
	selectedUser.value = {};
	GetUserDetails();
};

const backToUserDetails = () => {
	showDetails.value = true;
	showPostal.value = false;
	if (selectedUser.value && selectedUser.value.id) {
		viewDetails(selectedUser.value);
	}
};

const closePage = () => {
	showSuccess.value = false;
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
	transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
	transform: translateY(-5px);
}

/* Modal transitions */
.modal-enter-active {
	transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-active>div {
	animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-leave-active {
	transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-leave-active>div {
	animation: modalSlideOut 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from,
.modal-leave-to {
	opacity: 0;
}

@keyframes modalSlideIn {
	from {
		transform: scale(0.9) translateY(-10px);
		opacity: 0;
	}

	to {
		transform: scale(1) translateY(0);
		opacity: 1;
	}
}

@keyframes modalSlideOut {
	from {
		transform: scale(1) translateY(0);
		opacity: 1;
	}

	to {
		transform: scale(0.9) translateY(-10px);
		opacity: 0;
	}
}
</style>
